import type { ApiResponse } from '@shared/types';

const BASE = '/api';

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (res.status === 401) {
    // Redirect to login if session expired
    if (window.location.pathname !== '/' && window.location.pathname !== '/auth/verify') {
      window.location.href = '/';
    }
    throw new Error('Not authenticated');
  }

  const json: ApiResponse<T> = await res.json();

  if (!json.ok) {
    throw new Error(json.error || 'Request failed');
  }

  return json.data as T;
}

// Convenience methods
export const api = {
  get: <T>(path: string) => request<T>(path),

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),

  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  // For file uploads (no JSON content-type)
  upload: async <T>(path: string, formData: FormData): Promise<T> => {
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    const json: ApiResponse<T> = await res.json();
    if (!json.ok) throw new Error(json.error || 'Upload failed');
    return json.data as T;
  },
};
